import {
  TRACK_SHARE,
  TRACK_SHARE_ERROR,
  TRACK_SHARE_SUCCESS,
  TRACK_SHARES_LOADING,
  TRACK_SHARES_LOADING_ERROR,
  TRACK_SHARES_LOADING_SUCCESS,
} from '@disco/constants/ActionTypes'

import type { GroupId } from 'types/group'
import type { Recipient } from 'types/list'
import type { Track } from 'types/track'

import type { Thunk } from 'types/redux'
import api from '@disco/api/server'
import flatten from 'lodash/flatten'
import type { Dispatch } from 'redux'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'

import Intercom from '@disco/api/intercom'
import { parseServerError } from '@disco/utils/captureError'
import { pushUINotification } from '@disco/utils/notifications'
import { EMAIL, RECIPIENT_GROUP } from '@disco/constants/RecipientTypes'

/**
 * Loads all shares for a specific track.
 * @param trackId - ID of the track to get shares for
 * @returns Thunk that fetches track shares and handles errors
 */
export function getTrackShares(trackId: number): Thunk {
  return async (dispatch: Dispatch<any>) => {
    dispatch({ type: TRACK_SHARES_LOADING, id: trackId })
    try {
      const responseShares = await api.getTrackShares(trackId)
      dispatch({
        type: TRACK_SHARES_LOADING_SUCCESS,
        id: trackId,
        responseShares,
      })
    } catch (error) {
      pushUINotification({
        message: parseServerError(error.body),
        level: 'error',
      })

      dispatch({
        type: TRACK_SHARES_LOADING_ERROR,
        id: trackId,
        error,
        errorMessage: parseServerError(error.body),
      })
    }
  }
}

/**
 * Shares a track with specified recipients via email or URL generation.
 * @param track - Track to share
 * @param recipients - Array of recipients to share with
 * @param options - Share configuration options
 * @param options.manual - Whether this is a manual share (URL generation) or email share
 * @param options.message - Optional message to include with the share
 * @param options.subject - Optional subject line for email shares
 * @param options.template - Optional email template to use
 * @param options.territory_ids - Optional territory IDs for regional sharing
 * @param options.noPlaylistShareNotification - Whether to suppress share notifications
 * @returns Thunk that shares the track and handles analytics and notifications
 */
export function trackShare(
  track: Track,
  recipients: Array<Recipient>,
  {
    message,
    subject,
    manual,
    template,
    territory_ids,
    noPlaylistShareNotification,
  }: {
    manual: boolean
    message?: string
    subject?: string
    template?: string
    territory_ids?: GroupId[]
    noPlaylistShareNotification?: boolean
  },
): Thunk {
  return async dispatch => {
    dispatch({
      type: TRACK_SHARE,
      track: track,
      recipients: recipients,
    })

    const shareRecipients = recipients.map(recipient => {
      let result: Recipient | null = null

      if (recipient.type === EMAIL) {
        result = {
          email: recipient.email,
          type: recipient.type,
          name: recipient.name,
        }
      } else {
        result = {
          id: recipient.id,
          type: recipient.type,
          business_ids: recipient.business_ids ? recipient.business_ids : null,
        }
      }

      return result
    })

    let shareResult

    try {
      shareResult = await api.emailTrack({
        id: track.id,
        manual: manual,
        subject: subject,
        message: message,
        template: template,
        territory_ids,
        recipients: shareRecipients,
      })

      // we need to set recipients to original params as backend returns
      // recipients without businesses param, which is required if you want
      // to create email group later, add them manually

      const businesses = flatten(recipients.map(r => r.businesses)).filter(
        Boolean,
      )

      const normalizedShareResult = {
        ...shareResult,
        recipients: shareResult.recipients.map(recipient => {
          return {
            ...recipient,
            businesses:
              recipient.businesses ||
              (recipient.business_ids || [])
                .map(id => businesses.find(bu => bu?.id === id))
                .filter(Boolean),
          }
        }),
      }

      dispatch({
        type: TRACK_SHARE_SUCCESS,
        track,
        result: normalizedShareResult,
      })

      if (!noPlaylistShareNotification) {
        if (manual) {
          pushUINotification({
            message: 'Generated share URL!',
            level: 'success',
          })
        } else {
          pushUINotification({
            message: 'Shared track!',
            level: 'success',
          })
        }
      }

      const hasEmailGroups = recipients.some(r => r.type === RECIPIENT_GROUP)
      if (hasEmailGroups) {
        dispatch(sendMixpanelEvent('Share track via email group'))
      }

      if (message && message.length) {
        dispatch(sendMixpanelEvent('Add share message'))
      }

      if (manual) {
        Intercom.sendRaw('Assign share')
      } else {
        Intercom.sendRaw('Email share')
        dispatch(
          sendMixpanelEvent('Email share', {
            Receivers: [{
              team_inboxes: recipients.filter(r => r.type === 'business')
                .length,
              email_addresses: recipients.filter(r => r.type !== 'business')
                .length,
            }],
            Type: 'Track',
          }),
        )
      }

      return { ...normalizedShareResult }
    } catch (error) {
      dispatch({
        type: TRACK_SHARE_ERROR,
        playlist: track,
        error: error,
      })
      throw error
    }
  }
}
